import type {
  UseStreamTransport,
  UseStreamTransportPayload
} from '@langchain/langgraph-sdk/ui';

/**
 * Tenant + routing context for a single stream invocation. The transport
 * invokes {@link MeasuredBffTransportOptions.resolveContext} immediately
 * before each stream call so the panel can change agents, threads, or
 * tenants without rebuilding the stream controller.
 */
export interface MeasuredBffStreamContext {
  clientId: number;
  brandId: number;
  /** Fleet assistantId routed by the BFF's AgentConfigService. */
  assistantId: string;
  /**
   * Optional thread continuity. When set, the BFF appends the new user
   * message to the existing thread instead of starting a new run.
   */
  threadId?: string;
}

/**
 * Construction-time options for {@link MeasuredBffTransport}.
 *
 * The transport is meant to be constructed once per consumer (e.g. once
 * per `injectStream` field initializer in a component) and stay alive for
 * the consumer's lifetime. Per-request values come through
 * {@link resolveContext} and {@link resolveAuthToken}.
 */
export interface MeasuredBffTransportOptions {
  /**
   * Origin for the BFF — typically `environment.apiDomain`. The full
   * invoke-stream URL is constructed as
   * `${apiUrl}/api/v1/bff/agents/{clientId}/{brandId}/invoke-stream`.
   */
  apiUrl: string;
  /** Value forwarded as the `provider` header (Okta / Cognito / etc.). */
  ssoProvider: string;
  /**
   * Resolves the bearer token at request time. Mirrors AuthenticationInterceptor's
   * Okta-vs-dev branch: prod hits Okta id_token, localhost falls back to the
   * static dev JWT signed for the local API verifier.
   */
  resolveAuthToken: () => Promise<string>;
  /**
   * Resolves the active tenant + agent + thread for the next stream call.
   * Invoked synchronously inside `stream()` immediately before the fetch,
   * so the consumer can mutate routing state (e.g. by writing to signals)
   * up until `submit()` is called and have it picked up here.
   *
   * Throws `MissingStreamContextError` if the consumer hasn't published a
   * complete context yet — typical when the panel loads before
   * SelectionService is ready.
   */
  resolveContext: () => MeasuredBffStreamContext;
  /**
   * Optional hook called once per stream with the LangSmith `run_id` for
   * the new run, extracted from the first `metadata` SSE event LangGraph
   * Platform emits. This is the canonical run id used for `/feedback`
   * submission and LangSmith trace lookup.
   *
   * Tapping the wire directly here is more robust than reading
   * `stream.getMessagesMetadata(message).streamMetadata.run_id` at turn
   * finalize time — the SDK's per-message metadata is keyed by chunk id
   * (`lc_run--<uuid>`) and can be invalidated when the final `values`
   * event reconciles message ids during state persistence. The
   * `metadata` event always fires once at run start with the same
   * `run_id`, regardless of any subsequent reconciliation.
   */
  onRunMetadata?: (metadata: { run_id: string; attempt?: number }) => void;
}

/**
 * Raised by {@link MeasuredBffTransport.stream} when `resolveContext()`
 * returns a context missing required fields. Caller is expected to gate
 * `stream.submit()` on a "ready" signal so this is rarely observed at
 * runtime; we still throw rather than fail silently so misuse surfaces.
 */
export class MissingStreamContextError extends Error {
  constructor(missing: keyof MeasuredBffStreamContext) {
    super(
      `MeasuredBffTransport: stream context missing required field "${missing}"`
    );
    this.name = 'MissingStreamContextError';
  }
}

/**
 * `UseStreamTransport` adapter for measured-api's BFF SSE endpoint.
 *
 * The BFF emits LangGraph runtime events verbatim — `metadata`,
 * `messages/partial`, `updates`, `values`, `end`, `error` — which is the
 * same wire shape `@langchain/langgraph-sdk/ui` expects. This adapter is
 * therefore a thin pass-through of three concerns:
 *
 *   1. URL + body construction (tenant scoping, assistant routing)
 *   2. Auth header injection (Okta vs dev token branch)
 *   3. SSE byte stream → typed `{event, data, id?}` events
 *
 * Replaces the hand-rolled `AgentStreamService` SSE consumer.
 */
export class MeasuredBffTransport implements UseStreamTransport {
  constructor(private readonly options: MeasuredBffTransportOptions) {}

  async stream(
    payload: UseStreamTransportPayload
  ): Promise<AsyncGenerator<{ id?: string; event: string; data: unknown }>> {
    const ctx = this.options.resolveContext();
    if (!ctx.clientId) throw new MissingStreamContextError('clientId');
    if (!ctx.brandId) throw new MissingStreamContextError('brandId');
    if (!ctx.assistantId) throw new MissingStreamContextError('assistantId');

    const url = `${this.options.apiUrl}/api/v1/bff/agents/${ctx.clientId}/${ctx.brandId}/invoke-stream`;
    const token = await this.options.resolveAuthToken();

    const response = await fetch(url, {
      method: 'POST',
      signal: payload.signal,
      headers: {
        'Content-Type': 'application/json',
        // `*/*` instead of `text/event-stream` — Ts.ED's content-negotiation
        // middleware returns 406 if the route's @Header("Content-Type",...)
        // declaration doesn't match a strict Accept header. SSE framing is
        // parsed off the response Content-Type, not the request Accept,
        // so this relaxation has no observable consequence.
        Accept: '*/*',
        Authorization: `Bearer ${token}`,
        provider: this.options.ssoProvider
      },
      body: JSON.stringify({
        assistantId: ctx.assistantId,
        input: payload.input,
        ...(ctx.threadId ? { threadId: ctx.threadId } : {})
      })
    });

    if (!response.ok || !response.body) {
      throw new Error(
        `Agent stream failed: HTTP ${response.status} ${response.statusText}`
      );
    }

    return parseSseStream(response.body, this.options.onRunMetadata);
  }
}

// ── SSE framing ────────────────────────────────────────────────────────────
//
// Lifted from the deleted agent-stream.service.ts. SSE framing: each event
// is a sequence of `field: value` lines terminated by an empty line. We
// collect until `\n\n` and emit one event.

async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
  onRunMetadata?: (metadata: { run_id: string; attempt?: number }) => void
): AsyncGenerator<{ id?: string; event: string; data: unknown }> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let metadataReported = false;

  const maybeReportMetadata = (parsed: {
    event: string;
    data: unknown;
  }): void => {
    if (metadataReported || !onRunMetadata) return;
    if (parsed.event !== 'metadata') return;
    const data = parsed.data;
    if (
      data &&
      typeof data === 'object' &&
      'run_id' in data &&
      typeof (data as { run_id?: unknown }).run_id === 'string'
    ) {
      metadataReported = true;
      onRunMetadata(data as { run_id: string; attempt?: number });
    }
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const rawEvent = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseSseEvent(rawEvent);
        if (parsed) {
          maybeReportMetadata(parsed);
          yield parsed;
        }
        boundary = buffer.indexOf('\n\n');
      }
    }
    if (buffer.trim().length > 0) {
      const parsed = parseSseEvent(buffer);
      if (parsed) {
        maybeReportMetadata(parsed);
        yield parsed;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSseEvent(
  raw: string
): { id?: string; event: string; data: unknown } | null {
  let event = 'message';
  let id: string | undefined;
  const dataLines: string[] = [];

  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    } else if (line.startsWith('id:')) {
      id = line.slice(3).trim();
    }
  }

  if (dataLines.length === 0) return null;
  const dataStr = dataLines.join('\n');

  let data: unknown;
  try {
    data = JSON.parse(dataStr);
  } catch {
    data = dataStr;
  }
  return { id, event, data };
}
